import { API_ORIGIN } from '../config'
const API_BASE = `${API_ORIGIN}/api/superadmin`

// Sin sesión de superadmin el backend responde 404 (no 401): el panel no revela que existe.
async function request(path, { method = 'GET', body } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const error = new Error(err.error || 'REQUEST_FAILED')
    error.status = response.status
    // Código de referencia que el backend agrega a los errores 500 (se busca en Sistema).
    if (err.ref) error.ref = err.ref
    throw error
  }
  return response.json()
}

const qs = (params) => {
  const sp = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.set(k, v) })
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// --- Sistema ---
export const getSystemHealth = () => request('/system/health')
export const listSystemErrors = (includeReviewed) => request(`/system/errors${qs({ includeReviewed: includeReviewed ? '1' : '' })}`)
export const reviewSystemError = (id) => request(`/system/errors/${id}/review`, { method: 'POST', body: {} })
export const getCommunications = () => request('/system/communications')
// Sin id reenvía todos los fallidos y sin enviar; con status ('FAILED' | 'SKIPPED') solo ese grupo.
export const retryCommunications = ({ id, status } = {}) => request('/system/communications/retry', { method: 'POST', body: { id, status } })

// WhatsApp de la plataforma (un solo número de Parkos): estado, plantillas y conexión con Meta.
export const getWhatsAppPlatform = () => request('/system/whatsapp')
export const verifyWhatsApp = () => request('/system/whatsapp/verify', { method: 'POST', body: {} })
export const submitWhatsAppTemplates = () => request('/system/whatsapp/templates/submit', { method: 'POST', body: {} })
export const syncWhatsAppTemplates = () => request('/system/whatsapp/templates/sync', { method: 'POST', body: {} })

// --- Dashboard ---
export const getDashboard = () => request('/dashboard')

// --- Sesión ---
export const adminLogin = (email, password) => request('/auth/login', { method: 'POST', body: { email, password } })
export const adminLoginVerify = (challengeId, code) => request('/auth/login/verify', { method: 'POST', body: { challengeId, code } })
export const adminLoginResend = (email, password) => request('/auth/login/resend', { method: 'POST', body: { email, password } })
export const adminLogout = () => request('/auth/logout', { method: 'POST' })
// Devuelve el superadmin o null si no hay sesión (404).
export async function adminMe() {
  try {
    return await request('/auth/me')
  } catch (err) {
    if (err.status === 404) return null
    throw err
  }
}

// --- Parques ---
export const listParks = (search) => request(`/parks${qs({ search })}`)
export const createPark = (payload) => request('/parks', { method: 'POST', body: payload })
export const updatePark = (id, payload) => request(`/parks/${id}`, { method: 'PATCH', body: payload })
export const suspendPark = (id, reason) => request(`/parks/${id}/suspend`, { method: 'POST', body: { reason } })
export const reactivatePark = (id) => request(`/parks/${id}/reactivate`, { method: 'POST', body: {} })
export const deletePark = (id, confirmName, password) => request(`/parks/${id}/delete`, { method: 'POST', body: { confirmName, password } })
export const impersonatePark = (id, password) => request(`/parks/${id}/impersonate`, { method: 'POST', body: { password } })

// --- Usuarios ---
export const listUsers = (params) => request(`/users${qs(params)}`)
export const regenerateInvitation = (kind, id) => request(`/users/${kind}/${id}/invitation`, { method: 'POST', body: {} })
export const closeUserSessions = (kind, id) => request(`/users/${kind}/${id}/logout-all`, { method: 'POST', body: {} })
export const resetUserPassword = (kind, id, password) => request(`/users/${kind}/${id}/password-reset`, { method: 'POST', body: { password } })
export const suspendUser = (kind, id, reason) => request(`/users/${kind}/${id}/suspend`, { method: 'POST', body: { reason } })
export const reactivateUser = (kind, id) => request(`/users/${kind}/${id}/reactivate`, { method: 'POST', body: {} })
export const getUserLogins = (kind, id) => request(`/users/${kind}/${id}/logins`)
export const updateUser = (kind, id, body) => request(`/users/${kind}/${id}`, { method: 'PATCH', body })
export const addParkAdmin = (parkId, body) => request(`/parks/${parkId}/admins`, { method: 'POST', body })

// --- Bitácora ---
export const listAudit = (params) => request(`/audit${qs(params)}`)
export const getAuditFilters = () => request('/audit/filters')
export const getImpersonationDetail = (id) => request(`/audit/impersonations/${id}`)
export const auditCsvUrl = (params) => `${API_BASE}/audit/export.csv${qs(params)}`

// --- Facturación ---
export const listPlans = () => request('/billing/plans')
export const createPlan = (payload) => request('/billing/plans', { method: 'POST', body: payload })
export const updatePlan = (id, payload) => request(`/billing/plans/${id}`, { method: 'PATCH', body: payload })
export const listBillingParks = (search) => request(`/billing/parks${qs({ search })}`)
export const getBillingSummary = () => request('/billing/summary')
export const getParkPayments = (parkId) => request(`/billing/parks/${parkId}/payments`)
export const registerPayment = (parkId, payload) => request(`/billing/parks/${parkId}/payment`, { method: 'POST', body: payload })
export const assignPlan = (parkId, planId, nextBillingAt) => request(`/billing/parks/${parkId}/plan`, { method: 'POST', body: { planId, nextBillingAt } })
export const markParkOverdue = (parkId, reason) => request(`/billing/parks/${parkId}/overdue`, { method: 'POST', body: { reason } })
export const applyDiscount = (parkId, percent, until) => request(`/billing/parks/${parkId}/discount`, { method: 'POST', body: { percent, until } })
export const removeDiscount = (parkId) => request(`/billing/parks/${parkId}/discount/remove`, { method: 'POST', body: {} })